// ABOUTME: Presents a `media` content type to the data-table package as the file value it stores.
// ABOUTME: That package has no `media` in its form-field registry and throws when it meets one.

/**
 * `@ds/data-table` builds each column from the content type's own `type`, and its form-field
 * registry knows nothing about `media`: `buildEmptyValue` throws `Invalid form field type: media`
 * and the whole table fails to render. A media value *is* a media id, so the grid is handed
 * `image` — the column that previews it — rather than `file`, which only names it. A media that
 * is not an image has no preview to show and the renderer falls back to its name.
 *
 * `image` here is the **grid package's** vocabulary, not PIM's content type of the same name:
 * this translation survives PIM dropping `file` and `image`, and dies only when the package
 * learns `media` (tracked in techDebtTracker.md).
 *
 * Read-only on top, because the file cell editor uploads through the legacy `POST /api/media`
 * path: that writes a `Media` row and no MEDIA node, creating catalog content behind the
 * catalog's back. A media value is edited in the node form, where the media field owns the flow.
 *
 * Drop this once the data-table package knows the type — the mapping is a translation at the
 * boundary, not a statement about what `media` is.
 */
export const asGridContentType = <T extends { type?: string | null }>(contentType: T): T =>
    contentType?.type === 'media'
        ? { ...contentType, type: 'image', readOnly: true, mediaColumn: true }
        : contentType;

/**
 * Whether a column was a `media` one before the translation above. A cell with no content of its
 * own — a folder's asset — is rendered by the column's type, so it lands on the `image` renderer
 * and needs this to be handed to the media one instead.
 */
export const isMediaColumn = (contentType: unknown): boolean =>
    typeof contentType === 'object' &&
    contentType !== null &&
    'mediaColumn' in contentType &&
    contentType.mediaColumn === true;
